import { useCallback, useEffect, useState } from "react";
import { ArrowRightLeft, Search, Users } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { EmptyState, PageHeader, StatusPill } from "@/components/common";
import { rupiah } from "@/lib/format";

const MoveDialog = ({ p, cabang, onClose, onDone }) => {
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setTo(""); }, [p]);
  const submit = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/wilayah-petugas/${p.id}/move`, { cabang_id: to }); toast.success(`${p.name} dipindah ke ${data.cabang_name}`); onClose(); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={Boolean(p)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md" data-testid="move-petugas-dialog">
        <DialogHeader><DialogTitle className="font-heading">Pindah Kantor Cabang</DialogTitle>
          <DialogDescription>{p?.name} saat ini di {p?.cabang_name}. Resort lama dilepas dari petugas, lalu akun langsung pindah ke kantor cabang tujuan.</DialogDescription></DialogHeader>
        <Select value={to} onValueChange={setTo}>
          <SelectTrigger className="h-11" data-testid="move-target-select"><SelectValue placeholder="Pilih kantor cabang tujuan" /></SelectTrigger>
          <SelectContent>{cabang.filter((c) => c.id !== p?.cabang_id).map((c) => <SelectItem key={c.id} value={c.id} data-testid={`move-target-${c.id}`}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
        <DialogFooter><Button className="rounded-full h-11 px-6" disabled={!to || busy} onClick={submit} data-testid="move-submit">{busy ? "Memindahkan..." : "Pindahkan"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function DataPetugasPage() {
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  const [cab, setCab] = useState("all");
  const [mv, setMv] = useState(null);
  const load = useCallback(() => { api.get("/wilayah-petugas").then((r) => setD(r.data)).catch((e) => { toast.error(errMsg(e)); setD({ petugas: [], cabang: [] }); }); }, []);
  useEffect(() => { load(); }, [load]);
  const rows = (d?.petugas || []).filter((p) => (cab === "all" || p.cabang_id === cab) && (!q || `${p.name} ${p.email}`.toLowerCase().includes(q.toLowerCase())));
  return (
    <div>
      <PageHeader title="Data Petugas" />
      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-[200px]"><Search className="h-4 w-4 absolute left-3 top-3.5 text-muted-foreground" /><Input className="h-11 pl-9" placeholder="Cari petugas" value={q} onChange={(e) => setQ(e.target.value)} data-testid="petugas-search" /></div>
        <Select value={cab} onValueChange={setCab}>
          <SelectTrigger className="h-11 w-[220px]" data-testid="petugas-cabang-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Semua kantor cabang</SelectItem>{(d?.cabang || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      {!d ? <p className="text-muted-foreground">Memuat...</p> : rows.length === 0 ? <EmptyState icon={Users} title="Belum ada petugas" text="Petugas lapangan di wilayah Anda akan tampil di sini." testId="petugas-empty" /> : (
        <div className="panel !p-0 overflow-x-auto" data-testid="wilayah-petugas-table">
          <table className="w-full text-sm min-w-[760px]">
            <thead><tr className="text-left text-xs text-muted-foreground border-b"><th className="py-3 pl-5 font-medium">Nama Petugas</th><th className="py-3 px-3 font-medium">Kantor Cabang</th><th className="py-3 px-3 font-medium">Resort</th><th className="py-3 px-3 font-medium text-right">Saldo Modal</th><th className="py-3 px-3 font-medium">Status</th><th className="py-3 pr-5" /></tr></thead>
            <tbody>{rows.map((p) => (
              <tr key={p.id} className="border-b last:border-0" data-testid={`wilayah-petugas-row-${p.id}`}>
                <td className="py-3 pl-5"><p className="font-medium">{p.name}</p><p className="text-xs text-muted-foreground">{p.email}</p>
                  <Button size="sm" variant="outline" className="md:hidden mt-1 h-7 rounded-full text-xs" disabled={(d.cabang || []).length < 2} onClick={() => setMv(p)} data-testid={`move-petugas-mobile-${p.id}`}><ArrowRightLeft className="h-3 w-3 mr-1" />Pindah</Button></td>
                <td className="py-3 px-3" data-testid={`wilayah-petugas-cabang-${p.id}`}>{p.cabang_name}</td>
                <td className="py-3 px-3 text-xs">{p.resorts.join(", ") || "-"}</td>
                <td className="py-3 px-3 text-right font-mono">{rupiah(p.modal_balance || 0)}</td>
                <td className="py-3 px-3"><StatusPill active={p.active} /></td>
                <td className="py-3 pr-5 text-right"><Button size="sm" variant="outline" className="h-8 rounded-full" disabled={(d.cabang || []).length < 2} onClick={() => setMv(p)} data-testid={`move-petugas-${p.id}`}><ArrowRightLeft className="h-3.5 w-3.5 mr-1" />Pindah</Button></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      <MoveDialog p={mv} cabang={d?.cabang || []} onClose={() => setMv(null)} onDone={load} />
    </div>
  );
}
